import {
    EndorsementDetailsModel,
    EndorsementMultiLanguagePositionViewModel,
    EndorsementsClient,
    LanguageOnPrintedEndorsement,
    MultiLanguageString
} from "@/api-client";
import Handlebars from "handlebars";
import {isNullOrWhiteSpace} from "@/extensions/string.helpers";
import {PrintTemplateRenderOptions, toAbsoluteApiUrl} from "@/features/shared/CertificatePrintService";

Handlebars.registerHelper('helperMissing', function(...args: any[]) {
    const options = args[args.length - 1] as {
        name: string;
        data: { root: Record<string, unknown> };
        lookupProperty: (parent: unknown, key: string) => unknown;
    };
    const rootValue = options.lookupProperty(options.data.root, options.name);
    if (rootValue === null) {
        return "";
    }
    return `UNKNOWN(${options.name})`;
});
import {formatDate} from "@/formatting/dateFormatter";

export default async function renderEndorsementPrintTemplate(
    endorsement: EndorsementDetailsModel,
    printTemplateUrl: string,
    qrCodeUrl: string,
    page1?:string | null,
    page2?:string | null,
    options?: PrintTemplateRenderOptions,
): Promise<string> {

    let page1Full = null;
    let page2Full = null;

    if(page1?.startsWith("/") || page2?.startsWith("/")){
        const settings = (await import('@/main')).settings;
        page1Full = settings.misApiBaseUrl + page1;
        page2Full = settings.misApiBaseUrl + page2;
    }

    const signatureUrl = await toAbsoluteApiUrl(options?.transparentSignatureUrl) ?? endorsement.signatureUrl;
    const officialSignatureUrl = await toAbsoluteApiUrl(options?.transparentOfficialSignatureUrl)
        ?? endorsement.officialSignature?.url;

    const printTemplate = await fetch(printTemplateUrl, { method: 'GET' });

    function getValueOrDefault(value: string | undefined) {
        return isNullOrWhiteSpace(value) ? "-" : value;
    }

    function positionMap(position: EndorsementMultiLanguagePositionViewModel) {
        const positions = [];
        positions.push({
            post: position.positionName.estonian.toUpperCase(),
            responsibility: getValueOrDefault(position.responsibilities?.estonian),
            // eslint-disable-next-line @typescript-eslint/camelcase
            post_level: getValueOrDefault(position.positionLevelName.estonian),
            limitations: position.restrictions.map(r => r.estonian)
        });
        if (endorsement.languageOnPrint == LanguageOnPrintedEndorsement.EstonianAndEnglish){
            positions.push({
                post: position.positionName.english.toUpperCase(),
                responsibility: getValueOrDefault(position.responsibilities?.english),
                // eslint-disable-next-line @typescript-eslint/camelcase
                post_level: getValueOrDefault(position.positionLevelName.english),
                limitations: position.restrictions.map(r => r.english)
            });
        }
        return positions;
    }

    const limitations: {responsibilities: string | undefined, level: string, joinedLimitations: string[]} [] = [];

    //If responsibilities and levels match for different positions join limitations together to not write responsibilities/levels multiple times.

    for(const position of endorsement.positions){
        const matchingRestrictionEstonian = limitations.find(x => x.responsibilities == position.responsibilities?.estonian
            && x.level == position.positionLevelName.estonian);

        if (matchingRestrictionEstonian) {
            matchingRestrictionEstonian.joinedLimitations.push(position.restrictions.map(x => x.estonian).join(". "));
        }
        else {
            limitations.push({responsibilities: position.responsibilities?.estonian, level: position.positionLevelName.estonian,
                joinedLimitations: position.restrictions.length ? [position.restrictions.map(x => x.estonian).join(". ")] : []})
        }

        if (endorsement.languageOnPrint == LanguageOnPrintedEndorsement.EstonianAndEnglish){
            const matchingRestrictionEnglish = limitations.find(x => x.responsibilities == position.responsibilities?.english
                && x.level == position.positionLevelName.english);

            if (matchingRestrictionEnglish) {
                matchingRestrictionEnglish.joinedLimitations.push(position.restrictions.map(x => x.english).join(". "));
            }
            else {
                limitations.push({responsibilities: position.responsibilities?.english, level: position.positionLevelName.english,
                    joinedLimitations: position.restrictions.length ?  [position.restrictions.map(x => x.english).join(". ")] : []})
            }
        }
    }
    const positions = endorsement.positions.flatMap(positionMap);

    const template = Handlebars.compile(await printTemplate.text());
    /*eslint-disable */
    return template({
        SEERIANR: endorsement.documentNumber,
        TUNNISTUSNR: endorsement.certificateNumber,
        PERENIMI: endorsement.lastName,
        EESNIMI: endorsement.firstName,
        SYNNIKPV: formatDate(endorsement.birthDate),
        ISIKUKOOD: endorsement.personalCode,
        photo: endorsement.photoUrl,
        signature: signatureUrl,
        KEHTIVUSELOPP: formatDate(endorsement.validUntil),
        KEHTIVUSEALGUS: formatDate(endorsement.issuedOn),
        // Aliases matching the placeholder names used in certificate print templates.
        KLOPP: formatDate(endorsement.validUntil),
        VAKPV: formatDate(endorsement.issuedOn),
        AMETEST: endorsement.positions.map(p => p.positionName.estonian).join(", "),
        AMETENG: endorsement.positions.map(p => p.positionName.english).join(", "),
        LAEVANIMI: endorsement.shipNames?.join(", ") ?? "",
        SOIDULIINID: endorsement.shippingLineNames?.join(", ") ?? "",
        KOOLITUSEKUUPAEV: formatDate(endorsement.trainingDate),
        AMETIISIK: endorsement.officialSignature?.signerFullName,
        official_signature: officialSignatureUrl,
        posts: positions,
        restrictions: limitations,
        REEGLID: endorsement.stcwRuleCodes.join(", "),
        QRKOOD: qrCodeUrl,
        accessToken: endorsement.accessToken,
        TUNNISTUSRIIK: endorsement.certificateCountryNamePossessiveEst || "",
        TUNNISTUSRIIKENG: endorsement.certificateCountryNamePossessiveEng || "",

        PAGE1: page1Full,
        PAGE2: page2Full,
    })
    /*eslint-enable */
}